import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useMediaQuery } from '@/lib/hooks'

const SidebarContext = createContext<{ collapsed: boolean; toggle: () => void }>({
  collapsed: false,
  toggle: () => {},
})

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('tm-sidebar-collapsed') === '1'
    } catch { /* storage unavailable (e.g. Safari private browsing) */ }
    return false
  })

  useEffect(() => {
    try { localStorage.setItem('tm-sidebar-collapsed', collapsed ? '1' : '0') } catch { /* noop */ }
  }, [collapsed])

  function toggle() {
    setCollapsed(c => !c)
  }

  // Below 1024px the sidebar is forced to the 64px icon rail, regardless of the
  // persisted desktop preference. Driving this through the JS `collapsed` value
  // (rather than a CSS width override) means the inline width is already 64 and
  // there's no position/transform to animate across the breakpoint - so no flash.
  const isMobile = useMediaQuery('(max-width: 1024px)')
  const effectiveCollapsed = isMobile ? true : collapsed

  return (
    <SidebarContext.Provider value={{ collapsed: effectiveCollapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  return useContext(SidebarContext)
}
